/** Date, duration and money formatting for the twin panels. Times are UTC, as elsewhere in the app. */

const p2 = (n: number) => String(n).padStart(2, '0');
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export const DAY_MS = 86_400_000;

/** ms since epoch for ISO strings, "YYYY-MM-DD" and the shipped "YYYY/MM/DD HH:MM"; null when unparseable. */
export function parseDateMs(input: string | number | undefined | null): number | null {
  if (input === undefined || input === null) return null;
  if (typeof input === 'number') return Number.isFinite(input) ? input : null;
  const s = input.trim();
  if (!s) return null;
  const slash = /^(\d{4})\/(\d{2})\/(\d{2})(?:\s+(\d{2}):(\d{2}))?$/.exec(s);
  if (slash) {
    return Date.UTC(+slash[1], +slash[2] - 1, +slash[3], slash[4] ? +slash[4] : 0, slash[5] ? +slash[5] : 0);
  }
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (dateOnly) return Date.UTC(+dateOnly[1], +dateOnly[2] - 1, +dateOnly[3]);
  const t = Date.parse(s);
  return Number.isNaN(t) ? null : t;
}

/** 2025/12/31 00:00Z (the readings list rows). */
export function fmtDateTime(ms: number): string {
  const d = new Date(ms);
  return `${d.getUTCFullYear()}/${p2(d.getUTCMonth() + 1)}/${p2(d.getUTCDate())} ${p2(d.getUTCHours())}:${p2(d.getUTCMinutes())}Z`;
}

/** 22 Mar 2024 */
export function fmtDate(ms: number): string {
  const d = new Date(ms);
  return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}

/** Mar 2024 */
export function fmtMonthYear(ms: number): string {
  const d = new Date(ms);
  return `${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}

/** 03/22/24 (the graph's x labels). */
export function fmtShortDate(ms: number): string {
  const d = new Date(ms);
  return `${p2(d.getUTCMonth() + 1)}/${p2(d.getUTCDate())}/${String(d.getUTCFullYear()).slice(2)}`;
}

/** 16:03:21Z */
export function fmtTime(ms: number): string {
  const d = new Date(ms);
  return `${p2(d.getUTCHours())}:${p2(d.getUTCMinutes())}:${p2(d.getUTCSeconds())}Z`;
}

/** "just now", "12 s ago", "3 min ago", "2 h ago", "5 d ago". */
export function fmtAgo(deltaMs: number): string {
  const s = Math.max(0, Math.round(deltaMs / 1000));
  if (s < 5) return 'just now';
  if (s < 60) return `${s} s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 48) return `${h} h ago`;
  return `${Math.floor(h / 24)} d ago`;
}

/** Compact age without "ago": 12 s, 3 min, 2 h, 5 d. */
export function fmtAge(deltaMs: number): string {
  const s = Math.max(0, Math.round(deltaMs / 1000));
  if (s < 60) return `${s} s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  if (h < 48) return `${h} h`;
  return `${Math.floor(h / 24)} d`;
}

/** 7 d, 45 d, 1.2 yr. */
export function fmtDays(days: number): string {
  if (!Number.isFinite(days)) return '';
  if (days < 365) return `${Math.round(days)} d`;
  return `${(days / 365).toFixed(1)} yr`;
}

/** $15,000 */
export function fmtMoney(n: number): string {
  return `$${Math.round(n).toLocaleString('en')}`;
}

export function fmtInt(n: number): string {
  return Math.round(n).toLocaleString('en');
}

/** "fan_speed" to "Fan Speed". */
export function titleCase(id: string): string {
  return id.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}
